import { useEffect, useMemo, forwardRef } from 'react'
import { useStore } from '@tanstack/react-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { InstitucionCombobox } from '@/components/ui/institucion-combobox'
import { cn } from '@/lib/utils'
import type { CuentaConSaldo } from '@/services/cuentasService'
import {
  useCuentaForm,
  getFieldError,
  TIPOS_CUENTA,
  MONEDAS,
  type CuentaFormValues,
} from './forms'

interface CuentaFormNewProps {
  cuenta?: CuentaConSaldo | null
  onSubmit: (data: CuentaFormValues) => Promise<void> | void
  onCancel?: () => void
  isLoading?: boolean
  hideButtons?: boolean
  /** Lista de instituciones existentes para autocompletado */
  instituciones?: string[]
  /** Receives whether the form has unsaved changes. Reports false on unmount. */
  onDirtyChange?: (dirty: boolean) => void
}

export type { CuentaFormValues }

/**
 * Mapea una Cuenta de la BD a valores del formulario
 */
function mapCuentaToFormValues(cuenta: CuentaConSaldo): Partial<CuentaFormValues> {
  return {
    nombre: cuenta.nombre || '',
    tipo: (cuenta.tipo as CuentaFormValues['tipo']) || 'Banco',
    institucion: cuenta.institucion || null,
    cbu_cvu_alias: cuenta.cbu_cvu_alias || null,
    moneda: (cuenta.moneda as CuentaFormValues['moneda']) || 'ARS',
    observaciones: cuenta.observaciones || null,
    activa: cuenta.activa ?? true,
    saldo_inicial: cuenta.saldo_inicial ?? 0,
  }
}

export const CuentaFormNew = forwardRef<HTMLFormElement, CuentaFormNewProps>(
  function CuentaFormNew(
    { cuenta, onSubmit, onCancel, isLoading = false, hideButtons = false, instituciones = [], onDirtyChange },
    ref
  ) {
    // Mapear valores de cuenta existente
    const defaultValues = useMemo(
      () => (cuenta ? mapCuentaToFormValues(cuenta) : undefined),
      [cuenta]
    )

    const { form } = useCuentaForm({
      defaultValues,
      onSubmit,
    })

    const isDirty = useStore(form.store, (s) => s.isDirty)
    useEffect(() => {
      onDirtyChange?.(isDirty)
      return () => onDirtyChange?.(false)
    }, [isDirty, onDirtyChange])

    const handleFormSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      form.handleSubmit()
    }

    return (
      <form ref={ref} onSubmit={handleFormSubmit} className="space-y-6">
      {/* Nombre */}
      <form.Field name="nombre">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0
          return (
            <div className="space-y-2">
              <Label htmlFor="nombre" className="text-sm font-medium">
                Nombre *
              </Label>
              <Input
                id="nombre"
                placeholder="Ej: Banco Macro - Cta Cte"
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                className={cn(hasError && 'border-destructive focus-visible:ring-destructive')}
              />
              <p
                className={cn(
                  'text-sm min-h-[20px]',
                  hasError ? 'text-destructive' : 'text-transparent'
                )}
              >
                {getFieldError(field.state.meta.errors) || '\u00A0'}
              </p>
            </div>
          )
        }}
      </form.Field>

      {/* Tipo */}
      <form.Field name="tipo">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0
          return (
            <div className="space-y-2">
              <Label htmlFor="tipo" className="text-sm font-medium">
                Tipo *
              </Label>
              <Select
                value={field.state.value}
                onValueChange={(value) => field.handleChange(value as CuentaFormValues['tipo'])}
              >
                <SelectTrigger
                  id="tipo"
                  className={cn(hasError && 'border-destructive focus-visible:ring-destructive')}
                >
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CUENTA.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>
                      {tipo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasError && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )
        }}
      </form.Field>

      {/* Institución */}
      <form.Field name="institucion">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0
          return (
            <div className="space-y-2">
              <Label htmlFor="institucion" className="text-sm font-medium">
                Institución
              </Label>
              <InstitucionCombobox
                instituciones={instituciones}
                value={field.state.value}
                onValueChange={(value) => field.handleChange(value)}
                placeholder="Seleccionar o escribir institución..."
                hasError={hasError}
              />
              <p className="text-xs text-muted-foreground">
                Nombre del banco o institución financiera
              </p>
              {hasError && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )
        }}
      </form.Field>

      {/* CBU / CVU / Alias */}
      <form.Field name="cbu_cvu_alias">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0
          const value = field.state.value || ''
          const isCBU = /^\d{22}$/.test(value.replace(/\s/g, ''))

          return (
            <div className="space-y-2">
              <Label htmlFor="cbu_cvu_alias" className="text-sm font-medium">
                CBU / CVU / Alias
              </Label>
              <Input
                id="cbu_cvu_alias"
                placeholder="Ej: banco.macro.cuenta o 0000123456789012345678"
                value={value}
                onChange={(e) => field.handleChange(e.target.value || null)}
                onBlur={field.handleBlur}
                className={cn(hasError && 'border-destructive focus-visible:ring-destructive')}
              />
              <p className="text-xs text-muted-foreground">
                {isCBU
                  ? 'CBU válido (22 dígitos)'
                  : 'CBU para bancos, CVU para billeteras digitales, o alias personalizado'}
              </p>
              {hasError && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )
        }}
      </form.Field>

      {/* Moneda */}
      <form.Field name="moneda">
        {(field) => (
          <div className="space-y-2">
            <Label htmlFor="moneda" className="text-sm font-medium">
              Moneda
            </Label>
            <Select
              value={field.state.value}
              onValueChange={(value) => field.handleChange(value as CuentaFormValues['moneda'])}
            >
              <SelectTrigger id="moneda">
                <SelectValue placeholder="Seleccionar moneda" />
              </SelectTrigger>
              <SelectContent>
                {MONEDAS.map((moneda) => (
                  <SelectItem key={moneda} value={moneda}>
                    {moneda === 'ARS'
                      ? 'ARS - Peso Argentino'
                      : moneda === 'USD'
                        ? 'USD - Dólar'
                        : 'EUR - Euro'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </form.Field>

      {/* Observaciones */}
      <form.Field name="observaciones">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0
          return (
            <div className="space-y-2">
              <Label htmlFor="observaciones" className="text-sm font-medium">
                Observaciones
              </Label>
              <Textarea
                id="observaciones"
                placeholder="Ej: Cuenta exclusiva para pagos de impuestos"
                rows={3}
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value || null)}
                onBlur={field.handleBlur}
                className={cn(hasError && 'border-destructive focus-visible:ring-destructive')}
              />
              <p className="text-xs text-muted-foreground">
                Notas adicionales sobre el uso o características de esta cuenta
              </p>
              {hasError && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )
        }}
      </form.Field>

      {/* Botones */}
      {!hideButtons && (
        <div className="flex justify-end gap-3 pt-4 border-t">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancelar
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Guardando...' : cuenta ? 'Guardar cambios' : 'Crear Cuenta'}
          </Button>
        </div>
      )}
    </form>
    )
  }
)
